// Supabase OAuth callback — implicit flow (Phase 10).
//
// The page lands here with `#access_token=...&refresh_token=...` (implicit)
// or `?error=...`. Supabase-js's `detectSessionInUrl: true` will consume
// the URL fragment on first render and emit a `SIGNED_IN` event. We wait
// for that event (or a getSession() hit, whichever comes first) before
// calling /api/auth/sync-user so the axios interceptor can read the live
// access_token. Optional TOTP MFA: if nextLevel is aal2, challenge before sync.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { syncSupabaseUser, logout as backendLogout, redeemCoupon } from '../lib/api';
import {
  supabase,
  getSupabaseAccessToken,
  getMfaAssuranceLevel,
  listMfaFactors,
  needsMfaChallenge,
  signOutSupabase,
} from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import MfaChallengeForm from '../components/auth/MfaChallengeForm';

function readUrlError() {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  const hashSp = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  const err = sp.get('error') || hashSp.get('error');
  const desc = sp.get('error_description') || hashSp.get('error_description');
  if (!err && !desc) return null;
  return { err, desc };
}

async function waitForSession({ timeoutMs = 8000, intervalMs = 200 } = {}) {
  if (!supabase) return null;
  const start = Date.now();
  return new Promise((resolve) => {
    let done = false;
    const finish = (s) => { if (!done) { done = true; resolve(s); } };
    const sub = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) finish(session);
    });
    (async () => {
      while (!done && Date.now() - start < timeoutMs) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await supabase.auth.getSession();
        if (data?.session) { finish(data.session); return; }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, intervalMs));
      }
      finish(null);
    })();
    setTimeout(() => {
      try { sub.data.subscription.unsubscribe(); } catch { /* ignore */ }
    }, timeoutMs + 500);
  });
}

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);
  const [stage, setStage] = useState('Processing Google sign-in...');
  const [mfaFactorId, setMfaFactorId] = useState(null);

  const finishToDashboard = async () => {
    setStage('Syncing your account...');
    try {
      const res = await syncSupabaseUser();
      setUser(res.data);
      try {
        const code = sessionStorage.getItem('shipdesi_coupon');
        if (code && /^[A-Za-z0-9]{4,20}$/.test(code)) {
          await redeemCoupon(code);
          sessionStorage.removeItem('shipdesi_coupon');
        }
      } catch (coupErr) {
        // eslint-disable-next-line no-console
        console.warn('[auth-callback] coupon redemption skipped:', coupErr?.response?.data?.detail || coupErr.message);
      }
      navigate('/dashboard', { replace: true, state: { user: res.data } });
    } catch (syncErr) {
      // eslint-disable-next-line no-console
      console.error(
        '[auth-callback] /api/auth/sync-user failed:',
        syncErr?.response?.status,
        typeof syncErr?.response?.data?.detail === 'string'
          ? syncErr.response.data.detail
          : 'sync rejected',
      );
      const detail = (
        syncErr?.response?.data?.detail ||
        syncErr?.message ||
        'Sync request failed.'
      );
      setError(`Sign-in succeeded with Google but the backend rejected the token: ${detail}`);
      setTimeout(() => navigate('/login', { replace: true }), 4000);
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    if (!supabase) {
      setError('Authentication is not configured (missing REACT_APP_SUPABASE_URL).');
      return;
    }

    const urlErr = readUrlError();
    if (urlErr) {
      // eslint-disable-next-line no-console
      console.error('[auth-callback] URL contains error:', urlErr);
      setError(`Google sign-in was cancelled or failed: ${urlErr.desc || urlErr.err}`);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
      return;
    }

    const finish = async () => {
      setStage('Waiting for Supabase session...');
      const session = await waitForSession();

      if (!session?.access_token) {
        const detail = (
          'No Supabase session was returned after Google sign-in.\n\n' +
          'Most common cause: the redirect URL ' +
          `${window.location.origin}/auth/callback ` +
          'is NOT in your Supabase project Redirect URLs allowlist. ' +
          'Add it (and ensure Site URL is set to ' +
          `${window.location.origin}` +
          ') in the Supabase dashboard, save, then try again.'
        );
        // eslint-disable-next-line no-console
        console.error('[auth-callback] no session after timeout', detail);
        setError(detail);
        setTimeout(() => navigate('/login', { replace: true }), 6000);
        return;
      }

      setStage('Clearing legacy session...');
      try { await backendLogout(); } catch (e) { /* eslint-disable-next-line no-console */ console.warn('[auth-callback] backendLogout (non-fatal):', e); }

      const tokenSeen = await getSupabaseAccessToken();
      if (!tokenSeen) {
        const detail = (
          'Supabase reported a session, but the axios interceptor cannot read ' +
          'the access token. This usually means localStorage is partitioned ' +
          'between the iframe and top-level frame contexts. ' +
          'Try opening the preview in a fresh browser tab (not inside the ' +
          'platform preview iframe) — the URL is ' +
          `${window.location.origin}.`
        );
        setError(detail);
        setTimeout(() => navigate('/login', { replace: true }), 6000);
        return;
      }

      setStage('Checking security settings...');
      try {
        const { data: aal } = await getMfaAssuranceLevel();
        if (needsMfaChallenge(aal)) {
          const { data: factors } = await listMfaFactors();
          const factor = (factors?.totp || []).find((f) => f.status === 'verified') || factors?.totp?.[0];
          if (factor?.id) {
            setMfaFactorId(factor.id);
            return;
          }
        }
      } catch (mfaErr) {
        // eslint-disable-next-line no-console
        console.warn('[auth-callback] MFA check skipped:', mfaErr?.message || mfaErr);
      }

      await finishToDashboard();
    };
    finish();
  }, [navigate, setUser]);

  if (mfaFactorId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-4" data-testid="auth-callback-mfa">
        <div className="bg-white rounded-2xl border border-[#EBE8E4] p-10 max-w-md w-full">
          <MfaChallengeForm
            factorId={mfaFactorId}
            onVerified={finishToDashboard}
            onCancel={async () => {
              await signOutSupabase();
              navigate('/login', { replace: true });
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]" data-testid="auth-callback-screen">
      <div className="text-center max-w-xl px-6">
        {error ? (
          <div data-testid="auth-callback-error">
            <p className="text-base text-red-700 whitespace-pre-line" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {error}
            </p>
            <p className="mt-4 text-xs text-[#8F8984]">Redirecting to sign-in…</p>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-[#1A1B1E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-lg font-medium text-[#4A4D54]" style={{ fontFamily: 'Manrope, sans-serif' }}>{stage}</p>
          </>
        )}
      </div>
    </div>
  );
}
